import React, { useState } from 'react';
import { categories } from '../config/apps';
import { sendSolutionRequestEmail } from '../services/emailService';
import './RequestSolutionModal.css';

const RequestSolutionModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    department: '',
    customDepartment: '',
    title: '',
    description: '',
    contactName: '',
    contactEmail: '',
    priority: 'medium'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null

  // Department options based on existing categories plus additional ones
  const departmentOptions = [
    ...categories.map(cat => ({ 
      id: cat.id, 
      name: cat.name, 
      icon: cat.icon 
    })),
    { id: 'finance', name: 'Finance & Accounting', icon: '💰' },
    { id: 'marketing', name: 'Marketing & Communications', icon: '📢' },
    { id: 'legal', name: 'Legal & Compliance', icon: '⚖️' },
    { id: 'executive', name: 'Executive & Management', icon: '👔' },
    { id: 'facilities', name: 'Facilities & Operations', icon: '🏢' },
    { id: 'other', name: 'Other (Please specify)', icon: '📝' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.department || !formData.title || !formData.description || !formData.contactName || !formData.contactEmail) {
      alert('Please fill in all required fields.');
      return;
    }

    if (formData.department === 'other' && !formData.customDepartment) {
      alert('Please specify your department.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Prepare email data
      const emailData = {
        department: formData.department,
        customDepartment: formData.customDepartment,
        title: formData.title,
        description: formData.description,
        contactName: formData.contactName,
        contactEmail: formData.contactEmail,
        priority: formData.priority,
        type: 'request'
      };

      // Send email using Azure Email Service and save to admin system
      await sendSolutionRequestEmail(emailData);
      
      setSubmitStatus('success');
      
      // Reset form after successful submission
      setTimeout(() => {
        setFormData({
          department: '',
          customDepartment: '',
          title: '',
          description: '',
          contactName: '',
          contactEmail: '',
          priority: 'medium'
        });
        onClose();
        setSubmitStatus(null);
      }, 2000);

    } catch (error) {
      console.error('Error sending solution request:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };



  if (!isOpen) return null;

  return (
    <div className="request-modal-overlay" onClick={onClose}>
      <div className="request-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="request-modal-header">
          <h2>Help Us Automate! Submit a Request</h2>
          <button 
            className="request-modal-close" 
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {submitStatus === 'success' && (
          <div className="submit-success">
            <div className="success-icon">✅</div>
            <h3>Request Submitted Successfully!</h3>
            <p>Your solution request has been sent to the AI development team. You'll receive a response within 2-3 business days.</p>
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="submit-error">
            <div className="error-icon">❌</div>
            <h3>Submission Failed</h3>
            <p>There was an error sending your request. Please try again or contact IT support.</p>
          </div>
        )}

        {!submitStatus && (
          <form onSubmit={handleSubmit} className="request-form">
            <div className="form-section">
              <h3>📋 Request Details</h3>
              
              <div className="form-group">
                <label htmlFor="department">Department *</label>
                <select
                  id="department"
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  required
                  className="form-select"
                >
                  <option value="">Select your department...</option>
                  {departmentOptions.map(dept => (
                    <option key={dept.id} value={dept.id}>
                      {dept.icon} {dept.name}
                    </option>
                  ))}
                </select>
              </div>

              {formData.department === 'other' && (
                <div className="form-group">
                  <label htmlFor="customDepartment">Please specify your department *</label>
                  <input
                    type="text"
                    id="customDepartment"
                    name="customDepartment"
                    value={formData.customDepartment}
                    onChange={handleInputChange}
                    placeholder="e.g., Research & Development"
                    required
                    className="form-input"
                  />
                </div>
              )}

              <div className="form-group">
                <label htmlFor="title">Solution Title *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., Automated Inventory Management Bot"
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="priority">Priority Level</label>
                <select
                  id="priority"
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  <option value="low">🟢 Low - Nice to have</option>
                  <option value="medium">🟡 Medium - Helpful for efficiency</option>
                  <option value="high">🟠 High - Important for operations</option>
                  <option value="urgent">🔴 Urgent - Critical business need</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="description">Detailed Description *</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Please describe:
• What problem you're trying to solve
• Current manual process (if any)
• Expected benefits and outcomes
• Any specific requirements or constraints
• Estimated time savings or efficiency gains"
                  required
                  className="form-textarea"
                  rows="6"
                />
              </div>
            </div>

            <div className="form-section">
              <h3>👤 Contact Information</h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="contactName">Your Name *</label>
                  <input
                    type="text"
                    id="contactName"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleInputChange}
                    placeholder="Full name"
                    required
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contactEmail">Your Email *</label>
                  <input
                    type="email"
                    id="contactEmail"
                    name="contactEmail"
                    value={formData.contactEmail}
                    onChange={handleInputChange}
                    placeholder="your.email@knipper.com"
                    required
                    className="form-input"
                  />
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                onClick={onClose}
                className="btn-cancel"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="loading-spinner"></div>
                    Sending Request...
                  </>
                ) : (
                  <>
                    Submit Request
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default RequestSolutionModal;